import { useCallback, useEffect, useRef, useState } from 'react';
import { IMAGE_ZOOM_CONSTRAINTS } from '@v2/components/imageHandling/constants';
import {
  getImageViewController,
  getImageViewTransform,
  setImageViewTransform,
} from '../helpers/imageViewTransformStore';
import { useImageViewTransform } from './useImageViewTransform';
import { isWeb } from '@helpers';

export interface UseImageViewTransformControllerParams {
  withScalingPanning?: boolean;
  minZoom?: number;
  maxZoom?: number;
  rotationAngle?: number;
  /** documentId keys the shared store so Picture and DrawingCanvas share one source of truth. */
  documentId?: number;
}

interface ContainerLike {
  getBoundingClientRect: () => DOMRect;
}

/**
 * Single source of truth for image zoom/pan on web. State lives in the shared
 * `imageViewTransformStore` keyed by documentId — both ProgressiveImageWeb and
 * DrawingCanvas call this hook with the same documentId, so a wheel/pan/click
 * driven by either side updates the same transform.
 */
export const useImageViewTransformController = ({
  withScalingPanning,
  minZoom = IMAGE_ZOOM_CONSTRAINTS.min,
  maxZoom = IMAGE_ZOOM_CONSTRAINTS.max,
  rotationAngle = 0,
  documentId,
}: UseImageViewTransformControllerParams) => {
  const { zoom, panX, panY } = useImageViewTransform(documentId);

  const [isDragging, setIsDragging] = useState(false);
  // Anchors live in refs so they commit synchronously — setState wouldn't have landed before the next mousemove fires,
  // causing the delta to be computed against a stale anchor and producing visible jitter.
  const initialMouseX = useRef(0);
  const initialMouseY = useRef(0);
  const initialOffsetX = useRef(0);
  const initialOffsetY = useRef(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const writeTransform = useCallback(
    (next: { zoom: number; panX: number; panY: number }) => {
      if (documentId == null) return;
      setImageViewTransform(documentId, next);
    },
    [documentId]
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent | WheelEvent, container?: ContainerLike) => {
      e.preventDefault();

      if (!withScalingPanning) return;

      const containerEl = container ?? containerRef.current;
      if (!containerEl) return;

      const current = documentId != null ? getImageViewTransform(documentId) : { zoom, panX, panY };
      const rect = containerEl.getBoundingClientRect();
      const containerHeight = rect.height;
      const containerWidth = rect.width;
      const elementHeight = containerHeight;
      const elementWidth = containerWidth;
      const containerOffsetX = rect.left;
      const containerOffsetY = rect.top;
      const initialScale = current.zoom;
      const initialOffX = current.panX;
      const initialOffY = current.panY;
      const pointerOffsetX = e.clientX;
      const pointerOffsetY = e.clientY;
      const scaleDirection = e.deltaY < 0 ? 1 : -1;
      const scaleDifference = 0.2 * scaleDirection;
      const targetScale = Math.max(minZoom, Math.min(maxZoom, initialScale + scaleDifference));

      if ((initialScale === maxZoom && scaleDirection > 0) || (initialScale === minZoom && scaleDirection < 0)) {
        return;
      }

      const limitOffsetX = (elementWidth * targetScale - containerWidth) / 2;
      const limitOffsetY = (elementHeight * targetScale - containerHeight) / 2;

      let targetOffsetX = 0;
      let targetOffsetY = 0;

      if (targetScale <= minZoom) {
        targetOffsetX = 0;
        targetOffsetY = 0;
      } else {
        targetOffsetX =
          elementWidth * targetScale <= containerWidth
            ? 0
            : Math.max(
                -limitOffsetX,
                Math.min(
                  limitOffsetX,
                  initialOffX -
                    ((pointerOffsetX - containerOffsetX - containerWidth / 2 - initialOffX) / (targetScale - scaleDifference)) * scaleDifference
                )
              );
        targetOffsetY =
          elementHeight * targetScale <= containerHeight
            ? 0
            : Math.max(
                -limitOffsetY,
                Math.min(
                  limitOffsetY,
                  initialOffY -
                    ((pointerOffsetY - containerOffsetY - containerHeight / 2 - initialOffY) / (targetScale - scaleDifference)) * scaleDifference
                )
              );
      }

      writeTransform({ zoom: targetScale, panX: targetOffsetX, panY: targetOffsetY });
    },
    [documentId, maxZoom, minZoom, withScalingPanning, writeTransform]
  );

  const handleMouseDown = useCallback(
    (e: { clientX: number; clientY: number }) => {
      if (!withScalingPanning) return;
      const current = documentId != null ? getImageViewTransform(documentId) : { zoom, panX, panY };
      if (current.zoom > minZoom) {
        setIsDragging(true);
        initialMouseX.current = e.clientX;
        initialMouseY.current = e.clientY;
        initialOffsetX.current = current.panX;
        initialOffsetY.current = current.panY;
      }
    },
    [documentId, minZoom, withScalingPanning]
  );

  const handleMouseMove = useCallback(
    (e: { clientX: number; clientY: number }, container?: ContainerLike) => {
      if (!withScalingPanning) return;
      if (!isDragging) return;
      const current = documentId != null ? getImageViewTransform(documentId) : { zoom, panX, panY };
      if (current.zoom <= minZoom) return;
      const containerEl = container ?? containerRef.current;
      if (!containerEl) return;

      const rect = containerEl.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const elementWidth = containerWidth;
      const elementHeight = containerHeight;

      const limitOffsetX = (elementWidth * current.zoom - containerWidth) / 2;
      const limitOffsetY = (elementHeight * current.zoom - containerHeight) / 2;

      const mouseDeltaX = e.clientX - initialMouseX.current;
      const mouseDeltaY = e.clientY - initialMouseY.current;

      const shouldInvertX = rotationAngle === 90 || rotationAngle === 180;
      const shouldInvertY = rotationAngle === 180 || rotationAngle === 270;
      const effectiveDeltaX = shouldInvertX ? -mouseDeltaX : mouseDeltaX;
      const effectiveDeltaY = shouldInvertY ? -mouseDeltaY : mouseDeltaY;

      const targetOffsetX =
        elementWidth * current.zoom <= containerWidth
          ? 0
          : Math.max(-limitOffsetX, Math.min(limitOffsetX, initialOffsetX.current + effectiveDeltaX));
      const targetOffsetY =
        elementHeight * current.zoom <= containerHeight
          ? 0
          : Math.max(-limitOffsetY, Math.min(limitOffsetY, initialOffsetY.current + effectiveDeltaY));

      if (Math.abs(targetOffsetX) === Math.abs(limitOffsetX)) {
        initialOffsetX.current = targetOffsetX;
        initialMouseX.current = e.clientX;
      }
      if (Math.abs(targetOffsetY) === Math.abs(limitOffsetY)) {
        initialOffsetY.current = targetOffsetY;
        initialMouseY.current = e.clientY;
      }

      writeTransform({ zoom: current.zoom, panX: targetOffsetX, panY: targetOffsetY });
    },
    [documentId, isDragging, minZoom, rotationAngle, withScalingPanning, writeTransform]
  );

  const handleMouseUp = useCallback(() => {
    if (!withScalingPanning) return;
    setIsDragging(false);
  }, [withScalingPanning]);

  const handleDoubleClick = useCallback(
    (e: { clientX: number; clientY: number }, container?: ContainerLike) => {
      if (!withScalingPanning) return;

      // Native: drive the registered Zoomable controller. Toggles between 1× and the midpoint zoom,
      // matching the web double-click behaviour. Falls back to container center if focal coords miss.
      if (!isWeb) {
        const ctl = getImageViewController(documentId);
        if (!ctl) return;
        const current = documentId != null ? getImageViewTransform(documentId) : { zoom, panX, panY };
        if (current.zoom > minZoom) {
          ctl.reset();
          return;
        }
        const targetScale = (minZoom + maxZoom) / 2;
        const center = ctl.getContainerCenter();
        ctl.zoom({ scale: targetScale, x: center?.x ?? e.clientX, y: center?.y ?? e.clientY });
        return;
      }

      const containerEl = container ?? containerRef.current;
      if (!containerEl) return;

      const current = documentId != null ? getImageViewTransform(documentId) : { zoom, panX, panY };
      const rect = containerEl.getBoundingClientRect();
      const containerHeight = rect.height;
      const containerWidth = rect.width;
      const elementHeight = containerHeight;
      const elementWidth = containerWidth;
      const containerOffsetX = rect.left;
      const containerOffsetY = rect.top;
      const initialScale = current.zoom;
      const initialOffX = current.panX;
      const initialOffY = current.panY;
      const pointerOffsetX = e.clientX;
      const pointerOffsetY = e.clientY;

      const targetScale = current.zoom > minZoom ? minZoom : (minZoom + maxZoom) / 2;

      const limitOffsetX = (elementWidth * targetScale - containerWidth) / 2;
      const limitOffsetY = (elementHeight * targetScale - containerHeight) / 2;

      let targetOffsetX = 0;
      let targetOffsetY = 0;

      if (targetScale <= minZoom) {
        targetOffsetX = 0;
        targetOffsetY = 0;
      } else {
        const scaleDifference = targetScale - initialScale;
        targetOffsetX =
          elementWidth * targetScale <= containerWidth
            ? 0
            : Math.max(
                -limitOffsetX,
                Math.min(
                  limitOffsetX,
                  initialOffX - ((pointerOffsetX - containerOffsetX - containerWidth / 2 - initialOffX) / targetScale) * scaleDifference
                )
              );
        targetOffsetY =
          elementHeight * targetScale <= containerHeight
            ? 0
            : Math.max(
                -limitOffsetY,
                Math.min(
                  limitOffsetY,
                  initialOffY - ((pointerOffsetY - containerOffsetY - containerHeight / 2 - initialOffY) / targetScale) * scaleDifference
                )
              );
      }

      writeTransform({ zoom: targetScale, panX: targetOffsetX, panY: targetOffsetY });
    },
    [documentId, maxZoom, minZoom, withScalingPanning, writeTransform]
  );

  const zoomIn = useCallback(
    (e?: { preventDefault?: () => void; stopPropagation?: () => void }) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!withScalingPanning) return;
      const current = documentId != null ? getImageViewTransform(documentId) : { zoom, panX, panY };
      const nextZoom = Math.min(maxZoom, current.zoom + 0.5);
      if (!isWeb) {
        const ctl = getImageViewController(documentId);
        if (!ctl) return;
        const center = ctl.getContainerCenter();
        ctl.zoom({ scale: nextZoom, x: center?.x ?? 0, y: center?.y ?? 0 });
        return;
      }
      writeTransform({ ...current, zoom: nextZoom });
    },
    [documentId, maxZoom, withScalingPanning, writeTransform]
  );

  const zoomOut = useCallback(
    (e?: { preventDefault?: () => void; stopPropagation?: () => void }) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!withScalingPanning) return;
      const current = documentId != null ? getImageViewTransform(documentId) : { zoom, panX, panY };
      const nextZoom = Math.max(minZoom, current.zoom - 0.5);
      if (!isWeb) {
        const ctl = getImageViewController(documentId);
        if (!ctl) return;
        if (nextZoom <= minZoom) {
          ctl.reset();
          return;
        }
        const center = ctl.getContainerCenter();
        ctl.zoom({ scale: nextZoom, x: center?.x ?? 0, y: center?.y ?? 0 });
        return;
      }
      writeTransform({ ...current, zoom: nextZoom });
    },
    [documentId, minZoom, withScalingPanning, writeTransform]
  );

  // Non-passive wheel listener so preventDefault actually stops page scroll (React's synthetic onWheel
  // is sometimes passive in Chrome and silently drops preventDefault). Double-click is NOT attached
  // here — callers wire it themselves because in annotation mode it must hit-test the annotation
  // layer before it falls through to zoom.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      handleWheel(e, node);
    };
    node.addEventListener('wheel', wheelHandler, { passive: false });
    return () => {
      node.removeEventListener('wheel', wheelHandler);
    };
  }, [handleWheel]);

  return {
    panX,
    panY,
    zoom,
    isDragging,
    containerRef,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleDoubleClick,
    zoomIn,
    zoomOut,
  };
};
